import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router';
import {
  ArrowLeft,
  Upload,
  FileText,
  X,
  AlertCircle,
} from 'lucide-react';
import { useAnalyzeTrades, useUpdateStrategy, type AnalyzeResult } from '@/hooks/use-strategies';
import { friendlyError } from '@/lib/format';
import { useJob, ANALYSIS_STAGES } from '@/hooks/use-job';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { ForgeLoader } from '@/components/ui/forge-loader';
import { useQueryClient } from '@tanstack/react-query';
import { SymbolPicker } from '@/components/ui/symbol-picker';

const ACCEPTED_TYPES = ['.csv', '.json', '.html', '.htm', '.pdf'];

export default function StrategyUploadPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const analyzeTrades = useAnalyzeTrades();
  const updateStrategy = useUpdateStrategy();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [strategyName, setStrategyName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [symbol, setSymbol] = useState('');
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);

  // Track the analysis job
  const job = useJob<AnalyzeResult>('analysis', jobId);

  // When the job completes, navigate to the draft
  useEffect(() => {
    if (job.isComplete && job.result) {
      const draftId = job.result.draft?.id ?? (job.result as any).strategyId;
      if (draftId) {
        // Rename the draft if the user provided a custom name
        if (strategyName.trim()) {
          updateStrategy.mutateAsync({ id: draftId, name: strategyName.trim() }).finally(() => {
            qc.invalidateQueries({ queryKey: ['strategies'] });
            navigate(`/strategies/${draftId}`);
          });
        } else {
          qc.invalidateQueries({ queryKey: ['strategies'] });
          navigate(`/strategies/${draftId}`);
        }
      } else {
        setError('Analysis succeeded but no draft was created.');
        setJobId(null);
      }
    }
  }, [job.isComplete, job.result]);

  // Handle job failure
  useEffect(() => {
    if (job.isFailed) {
      setError(friendlyError(job.error, 'Analysis failed.'));
      setJobId(null);
    }
  }, [job.isFailed, job.error]);

  const handleFile = useCallback((f: File) => {
    const ext = f.name.slice(f.name.lastIndexOf('.')).toLowerCase();
    if (!ACCEPTED_TYPES.includes(ext)) {
      setError(`Unsupported file type: ${ext}. Use CSV, JSON, HTML, or PDF.`);
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      setError('File too large. Maximum size is 50 MB.');
      return;
    }
    setError('');
    setFile(f);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files[0];
      if (f) handleFile(f);
    },
    [handleFile],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a file.');
      return;
    }
    setError('');

    const formData = new FormData();
    formData.append('file', file);
    if (symbol.trim()) formData.append('symbol', symbol.trim());

    try {
      const result = await analyzeTrades.mutateAsync(formData);
      setJobId(result.jobId);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error)?.message ??
        'Analysis failed.';
      setError(friendlyError(typeof msg === 'string' ? msg : undefined, 'Analysis failed.'));
    }
  };

  const isProcessing = analyzeTrades.isPending || job.isLoading;
  const progressStage = job.progress?.stage as string | undefined;
  const progressLabel = progressStage
    ? ANALYSIS_STAGES[progressStage] || progressStage
    : 'Submitting...';

  return (
    <div className="space-y-5 max-w-lg mx-auto">
      {/* Back Link */}
      <Link
        to="/strategies"
        className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-ink-3 hover:text-orange transition-colors"
      >
        <ArrowLeft size={12} />
        All Strategies
      </Link>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-surface-2 flex items-center justify-center">
          <Upload size={18} className="text-orange" />
        </div>
        <div>
          <h1 className="text-lg font-extrabold tracking-tight">Upload Trade History</h1>
          <p className="text-xs text-ink-3">
            Upload your MT5 trade history to extract a strategy
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="glass-sm rounded-lg p-5 space-y-5">
        {/* Drop Zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
            dragOver
              ? 'border-orange bg-orange-bg'
              : file
                ? 'border-green/30 bg-green/5'
                : 'border-glass-border hover:border-orange/30 hover:bg-surface'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
            className="hidden"
          />

          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText size={24} className="text-green" />
              <div className="text-left">
                <p className="text-xs font-semibold">{file.name}</p>
                <p className="text-[10px] text-ink-3">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                }}
                className="w-6 h-6 rounded-md bg-surface flex items-center justify-center text-ink-3 hover:text-red transition-colors cursor-pointer"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <>
              <Upload size={32} className="text-ink-4 mx-auto mb-2" />
              <p className="text-xs font-semibold text-ink-2 mb-1">
                Drop file here or click to browse
              </p>
              <p className="text-[10px] text-ink-4">
                Supports .csv, .json, .html (MT5 report), and .pdf (Myfxbook)
              </p>
            </>
          )}
        </div>

        {/* Strategy Name */}
        <Input
          label="Strategy Name (optional)"
          placeholder="Auto-generated if left blank"
          value={strategyName}
          onChange={(e) => setStrategyName(e.target.value)}
        />

        {/* Symbol */}
        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1.5">Symbol (optional)</label>
          <SymbolPicker
            value={symbol}
            onChange={setSymbol}
            placeholder="Auto-detected from file"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 bg-red-bg rounded-md px-3 py-2">
            <AlertCircle size={14} className="text-red flex-shrink-0" />
            <p className="text-[10px] text-red font-semibold">{error}</p>
          </div>
        )}

        {/* Progress */}
        {job.isLoading && job.progress && (
          <div className="flex items-center gap-2">
            <Spinner size="sm" />
            <span className="text-[10px] text-ink-3 font-semibold">{progressLabel}</span>
          </div>
        )}

        {/* Submit */}
        <Button type="submit" className="w-full" disabled={isProcessing || !file}>
          {isProcessing ? (
            <>
              <Spinner size="sm" />
              {progressLabel}
            </>
          ) : (
            <>
              <Upload size={14} />
              Analyze Trades
            </>
          )}
        </Button>
      </form>

      <ForgeLoader open={isProcessing} />
    </div>
  );
}
